import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LANDING_URL, AUTH_URL } from '../lib/service-registry';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * MYB-371: AuthContext — Platform Token cookie as sole auth source.
 *
 * The Platform Token (PT) is a .tscorp.ai domain cookie set by auth.tscorp.ai.
 * Since hr.tscorp.ai is a subdomain, the browser sends this cookie automatically.
 * The backend middleware validates it and sets request.state.platform_user.
 *
 * Auth flow:
 *   1. On mount, call GET /api/auth/me (withCredentials) — backend reads PT cookie
 *   2. If authenticated, populate user state from the response
 *   3. If not, show login page (redirect to Azure AD SSO)
 *   4. Logout: redirect to auth.tscorp.ai/api/signout (clears PT cookie)
 *
 * NO sessionStorage tokens. NO Authorization header injection.
 */

interface AuthUser {
  id: number;
  username: string;
  role: string;
  display_name?: string;
  employee_id?: string;
  company_id?: number;
}

interface AuthContextType {
  user: AuthUser | null;
  /** @deprecated MYB-371: Always null — PT cookie is used instead of Bearer tokens */
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAccessDenied: boolean;
  // TSCORP-485: true when the SSO user has no matching HR employee record
  isNoEmployeeRecord: boolean;
  // True when the user heads at least one org unit (gates leave approval tab)
  hasLeaveApprovalCapability: boolean;
  ssoUserName: string | null;
  ssoUserEmail: string | null;
  login: (email: string, password: string) => Promise<void>;
  devLogin: (role: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // True when the user has a valid token but the backend rejected them (unregistered / no permission)
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  // TSCORP-485: True when SSO user is valid but has no HR employee record
  const [isNoEmployeeRecord, setIsNoEmployeeRecord] = useState(false);
  // True when user heads at least one org unit — gates leave approval tab
  const [hasLeaveApprovalCapability, setHasLeaveApprovalCapability] = useState(false);
  // SSO user identity (from Platform Token) for display in NoEmployeeRecord screen
  const [ssoUserName, setSsoUserName] = useState<string | null>(null);
  const [ssoUserEmail, setSsoUserEmail] = useState<string | null>(null);

  const logout = useCallback(() => {
    setUser(null);
    setIsNoEmployeeRecord(false);
    setIsAccessDenied(false);
    setHasLeaveApprovalCapability(false);
    // MYB-381: Unified logout — redirect to confirmation page first
    window.location.href = `${LANDING_URL}/hub/logout`;
  }, []);

  // MYB-371: On mount, check authentication via PT cookie by calling /api/auth/me
  useEffect(() => {
    const checkAuth = async () => {
      // Handle SSO error callback params (from redirect)
      const params = new URLSearchParams(window.location.search);
      const ssoError = params.get('auth_error');
      if (ssoError) {
        const errorMessages: Record<string, string> = {
          unauthorized_email: '허용되지 않은 이메일입니다.',
          token_exchange_failed: '인증 처리에 실패했습니다. 다시 시도해주세요.',
          graph_api_failed: '사용자 정보를 가져올 수 없습니다.',
          no_email: '이메일 정보를 가져올 수 없습니다.',
          no_code: '인증 코드를 받지 못했습니다.',
        };
        setError(errorMessages[ssoError] || `인증 오류: ${ssoError}`);
        window.history.replaceState({}, '', window.location.pathname);
        setIsLoading(false);
        return;
      }

      // Check PT cookie via backend /api/auth/me (with retry on 429)
      const MAX_RETRIES = 3;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const res = await axios.get(`${API_BASE_URL}/api/auth/me`, {
            withCredentials: true,
            timeout: 10000,
          });
          const data = res.data;

          const payload = data && typeof data === 'object' && 'success' in data && 'data' in data
            ? data.data
            : data;

          if (payload?.authenticated && payload?.user) {
            const u = payload.user;
            setUser(u);
            setSsoUserName(u.display_name || null);
            setSsoUserEmail(u.username || null);

            if (payload.has_employee_record === false) {
              setIsNoEmployeeRecord(true);
            } else {
              setIsNoEmployeeRecord(false);
            }
            setHasLeaveApprovalCapability(payload.has_leave_approval_capability === true);
            setIsAccessDenied(false);
          } else {
            setUser(null);
          }
          break;
        } catch (err: any) {
          const status = err?.response?.status;
          if (status === 429 && attempt < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
            continue;
          }
          if (status === 403) {
            setIsAccessDenied(true);
          } else {
            setUser(null);
          }
          break;
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const devLogin = async (role: string) => {
    setError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/dev-login`,
        { role },
        { timeout: 10000 }
      );
      const d = response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data
        ? response.data.data
        : response.data;
      // Dev login returns a user object — in dev mode we set user directly
      // (dev-login endpoint sets a cookie or we use the response)
      if (d?.user) {
        setUser(d.user);
      }
    } catch (err: any) {
      const errData = err.response?.data;
      const unwrapped = errData && typeof errData === 'object' && 'success' in errData && 'data' in errData
        ? errData.data
        : errData;
      const message = unwrapped?.detail || '테스트 로그인 실패';
      setError(message);
      throw new Error(message);
    }
  };

  const login = async (_email: string, _password: string) => {
    const authBase = AUTH_URL;
    const callbackUrl = encodeURIComponent(window.location.origin);
    window.location.href = `${authBase}/login?callbackUrl=${callbackUrl}`;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token: null, // MYB-371: deprecated — PT cookie is used instead
        isAuthenticated: !!user,
        isLoading,
        isAccessDenied,
        isNoEmployeeRecord,
        hasLeaveApprovalCapability,
        ssoUserName,
        ssoUserEmail,
        login,
        devLogin,
        logout,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
